<template>
  <div class="rq-tool">
    <!-- Connection -->
    <div class="rq-card">
      <div class="rq-card-hd">
        <span class="rq-dot rq-dot-blue" />
        <span>{{ $t('tool.RedisQuery.title') }}</span>
        <span class="rq-badge rq-badge-on" v-if="connected">{{ $t('tool.RedisQuery.connected') }} {{ conn.host }}:{{ conn.port }}</span>
        <el-radio-group v-model="mode" size="mini" class="rq-mode-switch" @change="onModeChange">
          <el-radio-button label="direct">{{ $t('tool.RedisQuery.directMode') }}</el-radio-button>
          <el-radio-button label="agent">{{ $t('tool.RedisQuery.agentMode') }}</el-radio-button>
        </el-radio-group>
      </div>

      <!-- Direct mode -->
      <div class="rq-form" v-if="mode==='direct'">
        <div class="rq-row">
          <div class="rq-field" style="flex:2"><label>{{ $t('tool.RedisQuery.host') }}</label><el-input v-model="conn.host" size="small" placeholder="127.0.0.1" /></div>
          <div class="rq-field"><label>{{ $t('tool.RedisQuery.port') }}</label><el-input v-model="conn.port" size="small" placeholder="6379" /></div>
          <div class="rq-field"><label>{{ $t('tool.RedisQuery.db') }}</label><el-select v-model="conn.db" size="small" style="width:100%"><el-option v-for="i in 16" :key="i-1" :label="'DB'+(i-1)" :value="i-1" /></el-select></div>
        </div>
        <div class="rq-field"><label>{{ $t('tool.RedisQuery.password') }}</label><el-input v-model="conn.password" size="small" type="password" show-password /></div>
        <div>
          <el-button type="primary" size="small" @click="doConnect">{{ $t('tool.RedisQuery.btnConnect') }}</el-button>
          <el-button size="small" @click="disconnect" v-if="connected">{{ $t('tool.RedisQuery.btnDisconnect') }}</el-button>
        </div>
      </div>

      <!-- Agent mode -->
      <div class="rq-form" v-if="mode==='agent'">
        <div class="rq-agent-help">
          <i class="el-icon-info" /> {{ $t('tool.RedisQuery.agentHelp') }}
          <a :href="agentDownloadUrl" target="_blank" class="rq-agent-dl">{{ $t('tool.RedisQuery.downloadAgent') }}</a>
        </div>
        <div class="rq-row">
          <div class="rq-field"><label>{{ $t('tool.RedisQuery.sessionId') }}</label><el-input v-model="conn.sessionId" size="small" :placeholder="$t('tool.RedisQuery.phSessionId')" /></div>
        </div>
        <div class="rq-row">
          <div class="rq-field" style="flex:2"><label>{{ $t('tool.RedisQuery.host') }}</label><el-input v-model="conn.host" size="small" placeholder="127.0.0.1" /></div>
          <div class="rq-field"><label>{{ $t('tool.RedisQuery.port') }}</label><el-input v-model="conn.port" size="small" placeholder="6379" /></div>
          <div class="rq-field"><label>{{ $t('tool.RedisQuery.db') }}</label><el-select v-model="conn.db" size="small" style="width:100%"><el-option v-for="i in 16" :key="i-1" :label="'DB'+(i-1)" :value="i-1" /></el-select></div>
        </div>
        <div class="rq-field"><label>{{ $t('tool.RedisQuery.password') }}</label><el-input v-model="conn.password" size="small" type="password" show-password /></div>
        <div>
          <el-button type="primary" size="small" @click="doConnect" :loading="connecting" :disabled="!conn.sessionId">
            {{ agentConnected ? $t('tool.RedisQuery.reconnect') : $t('tool.RedisQuery.btnConnect') }}
          </el-button>
          <el-button size="small" @click="disconnect" v-if="connected">{{ $t('tool.RedisQuery.btnDisconnect') }}</el-button>
        </div>
      </div>
    </div>

    <!-- Workspace (commands + input + result) -->
    <div class="rq-card" v-if="connected">
      <div class="rq-card-hd">
        <span class="rq-dot rq-dot-purple" />
        <span>{{ $t('tool.RedisQuery.command') }}</span>
        <span class="rq-card-hint">{{ $t('tool.RedisQuery.hintReadonly') }}</span>
      </div>
      <div class="rq-tags" v-if="quickCmds.length">
        <el-tag v-for="cmd in quickCmds" :key="cmd" size="small" class="rq-tag" @click="command=cmd">{{ cmd }}</el-tag>
      </div>
      <div class="rq-input-row">
        <el-input v-model="command" size="small" placeholder="KEYS *" class="rq-cmd-input" @keyup.enter="execute" />
        <el-button type="primary" size="small" @click="execute" :loading="loading">{{ $t('tool.RedisQuery.btnRun') }}</el-button>
      </div>
      <div class="rq-cmd-hint">{{ $t('tool.RedisQuery.hintCommands') }}</div>
    </div>

    <!-- Result -->
    <div class="rq-card" v-if="result">
      <div class="rq-card-hd">
        <span class="rq-dot rq-dot-green" />
        <span>{{ $t('tool.RedisQuery.result') }}</span>
        <span class="rq-card-hint">{{ result.command }}</span>
        <button class="rq-copy-btn" @click="copyResult" :title="$t('tool.SqlQuery.copyResult')">
          <i class="el-icon-document-copy" />
        </button>
      </div>

      <!-- string -->
      <div v-if="resType==='string'" class="rq-result-wrap">
        <div class="rq-str-meta">{{ resSize }}</div>
        <div class="rq-str-val" :class="{ 'rq-str-fold': isLongStr && !strExpanded }" ref="strBox">
          <pre v-if="isJson">{{ resJson }}</pre>
          <span v-else>{{ resStr }}</span>
        </div>
        <div class="rq-str-acts" v-if="isLongStr || isJson">
          <el-button size="mini" @click="copyResult">{{ $t('tool.SqlQuery.copyResult') }}</el-button>
          <el-button v-if="isLongStr" size="mini" @click="strExpanded=!strExpanded">
            {{ strExpanded ? $t('tool.RedisQuery.collapse') : $t('tool.RedisQuery.expandAll') }}
          </el-button>
        </div>
      </div>

      <!-- list -->
      <div v-else-if="resType==='list'" class="rq-result-wrap">
        <div class="rq-type-hd">{{ resList.length }} {{ $t('tool.RedisQuery.items') }}</div>
        <div class="rq-list">
          <div v-for="(v,i) in pagedList" :key="i" class="rq-list-item" :class="{ 'rq-stripe': i%2===1 }">
            <span class="rq-idx">{{ i }}</span><code>{{ v }}</code>
          </div>
        </div>
        <el-button v-if="resList.length > pageSize" size="mini" class="rq-more-btn" @click="listPage+=pageSize;pageSize*=2">
          {{ $t('tool.RedisQuery.showMore') }} ({{ Math.min(resList.length - pagedList.length, pageSize) }})
        </el-button>
      </div>

      <!-- map -->
      <div v-else-if="resType==='map'" class="rq-result-wrap">
        <div class="rq-type-hd">
          {{ mapKeys.length }} {{ $t('tool.RedisQuery.fields') }}
          <el-input v-if="mapKeys.length > 8" v-model="mapFilter" size="mini" :placeholder="$t('tool.RedisQuery.filterField')" clearable class="rq-map-search" />
        </div>
        <table class="rq-table">
          <thead><tr><th>Field</th><th>Value</th></tr></thead>
          <tbody>
            <tr v-for="k in filteredMapKeys" :key="k" class="rq-map-row">
              <td><code>{{ k }}</code></td>
              <td><code>{{ resMap[k] }}</code></td>
            </tr>
          </tbody>
        </table>
        <div v-if="filteredMapKeys.length===0" class="rq-empty">{{ $t('tool.RedisQuery.noMatch') }}</div>
      </div>

      <!-- nil -->
      <div v-else-if="resStr==='(nil)'" class="rq-result-wrap">
        <div class="rq-nil">{{ $t('tool.RedisQuery.keyNotExist') }}</div>
      </div>

      <!-- fallback (int / bool / raw) -->
      <div v-else class="rq-result-wrap">
        <div class="rq-raw-val">{{ resStr }}</div>
      </div>
    </div>

    <!-- Error -->
    <div class="rq-card rq-card-err" v-if="error">
      <div class="rq-err">{{ error }}</div>
    </div>
  </div>
</template>

<script>
import request from '@/api/request'

export default {
  name:'RedisQueryTool',props:{config:{type:Object,default:()=>({})}},
  data:()=>({
    mode:'direct',
    conn:{host:'127.0.0.1',port:'6379',db:0,password:'',sessionId:''},
    connected:false,connecting:false,
    agentConnected:false,
    command:'',result:null,error:'',loading:false,
    ws:null,
    heartbeatTimer:null,reconnectTimer:null,reconnectDelay:0,intentionalClose:false,
    strExpanded:false,mapFilter:'',listPage:50,pageSize:50,
    quickCmds:['KEYS *','GET mykey','TYPE mykey','TTL mykey','EXISTS mykey','DBSIZE','HGETALL myhash','HKEYS myhash','HVALS myhash','LRANGE mylist 0 -1','LLEN mylist','SMEMBERS myset','SCARD myset','ZRANGE myzset 0 -1','ZCARD myzset','PING']
  }),
  computed:{
    agentDownloadUrl(){
      return 'https://onltool.site/static/agent.exe'
    },
    resType(){ const r=this.result; return r?(r.resultType||r.type):'' },
    resStr(){ const r=this.result; return r?String(r.result==null?'':r.result):'' },
    resSize(){
      const s=this.resStr; if(!s)return ''
      const b=new Blob([s]).size
      return b<1024?b+' B':b<1048576?(b/1024).toFixed(1)+' KB':(b/1048576).toFixed(1)+' MB'
    },
    isLongStr(){ return this.resStr.length>200 },
    isJson(){
      const s=this.resStr; if(!s||s.length<2)return false
      return (s[0]==='{'&&s[s.length-1]==='}')||(s[0]==='['&&s[s.length-1]===']')
    },
    resJson(){
      try{ return JSON.stringify(JSON.parse(this.resStr),null,2) }catch{ return this.resStr }
    },
    resList(){ const r=this.result; return r&&Array.isArray(r.result)?r.result:[] },
    pagedList(){ return this.resList.slice(0,this.listPage) },
    resMap(){ const r=this.result; return r&&typeof r.result==='object'&&!Array.isArray(r.result)?r.result:{} },
    mapKeys(){ return Object.keys(this.resMap) },
    filteredMapKeys(){
      if(!this.mapFilter)return this.mapKeys
      const q=this.mapFilter.toLowerCase()
      return this.mapKeys.filter(k=>k.toLowerCase().includes(q)||String(this.resMap[k]).toLowerCase().includes(q))
    }
  },
  beforeDestroy(){
    this.closeWs()
  },
  methods:{
    closeWs(){
      this.stopHeartbeat();this.clearReconnect()
      this.intentionalClose=true
      if(this.ws){ this.ws.close(); this.ws=null }
    },
    onModeChange(){
      this.disconnect()
    },
    disconnect(){
      this.closeWs()
      this.connected=false; this.agentConnected=false
      this.result=null; this.error=''
      this.connecting=false; this.loading=false
    },
    startHeartbeat(){
      this.stopHeartbeat()
      this.heartbeatTimer=setInterval(()=>{
        if(this.ws&&this.ws.readyState===WebSocket.OPEN){
          this.ws.send(JSON.stringify({type:'ping'}))
        }
      },15000)
    },
    stopHeartbeat(){
      if(this.heartbeatTimer){clearInterval(this.heartbeatTimer);this.heartbeatTimer=null}
    },
    clearReconnect(){
      if(this.reconnectTimer){clearTimeout(this.reconnectTimer);this.reconnectTimer=null}
    },
    scheduleReconnect(){
      if(this.mode!=='agent'||this.intentionalClose)return
      if(this.reconnectDelay>=3){
        this.error=this.$t('tool.RedisQuery.reconnectFailed')
        this.connected=false; this.stopHeartbeat(); this.ws=null
        return
      }
      const delay=Math.min(1000*Math.pow(2,this.reconnectDelay),30000)
      this.reconnectDelay++
      this.reconnectTimer=setTimeout(()=>{
        this.error=this.$t('tool.RedisQuery.reconnecting')
        this.connectViaAgent()
      },delay)
    },
    async doConnect(){
      if(!this.conn.host)return
      if(this.mode==='agent'&&!this.conn.sessionId)return
      this.connecting=true;this.error='';this.result=null

      if(this.mode==='agent'){
        this.connectViaAgent()
      }else{
        this.connectDirect()
      }
    },
    async connectDirect(){
      this.closeWs()
      try{
        const res=await request({url:'/api/pub/redis/query',method:'post',data:{
          host:this.conn.host,port:Number(this.conn.port)||6379,
          password:this.conn.password,db:this.conn.db,command:'PING'
        }})
        const d=res.data
        if(d.error){this.error=d.error;this.connected=false}else{this.connected=true;this.result=d}
      }catch(e){this.error=this.$t('tool.RedisQuery.errConnect')+': '+(e.message||'')}
      this.connecting=false
    },
    connectViaAgent(){
      this.stopHeartbeat()
      const protocol=location.protocol==='https:'?'wss:':'ws:'
      const url=protocol+'//'+location.host+'/ws/browser/'+this.conn.sessionId
      this.intentionalClose=false
      const old=this.ws; if(old){old.onclose=null;old.close()}
      this.ws=new WebSocket(url)
      this.ws.onopen=()=>{
        this.reconnectDelay=0
        this.agentConnected=true
        this.error=''
        this.startHeartbeat()
        this.ws.send(JSON.stringify({
          type:'connect',
          host:this.conn.host,
          port:Number(this.conn.port)||6379,
          password:this.conn.password,
          db:this.conn.db
        }))
      }
      this.ws.onmessage=(e)=>{
        try{const msg=JSON.parse(e.data);this.handleAgentMsg(msg)}catch(ex){}
      }
      this.ws.onerror=()=>{
        if(!this.reconnectDelay){
          this.error=this.$t('tool.RedisQuery.errAgentConnect')
          this.connecting=false
        }
      }
      this.ws.onclose=()=>{
        this.agentConnected=false; this.loading=false
        if(!this.intentionalClose){
          this.scheduleReconnect()
        }else{
          this.connected=false;this.ws=null
        }
      }
    },
    handleAgentMsg(msg){
      switch(msg.type){
        case 'connected':
          this.connected=true; this.connecting=false
          break
        case 'result':
          this.result=msg
          this.loading=false
          break
        case 'error':
          this.error=msg.message||'Unknown error'
          this.connecting=false; this.loading=false
          break
        case 'agentDisconnected':
          this.error=this.$t('tool.RedisQuery.agentDisconnected')
          break
        case 'waitingAgent':
          this.error=msg.message||'Agent disconnected, waiting...'
          break
        case 'agentReconnected':
          this.error=''
          this.ws.send(JSON.stringify({
            type:'connect',host:this.conn.host,
            port:Number(this.conn.port)||6379,
            password:this.conn.password,db:this.conn.db
          }))
          break
        default:break
      }
    },
    async execute(){
      this.error='';this.result=null;if(!this.command.trim())return;this.loading=true
      this.strExpanded=false;this.mapFilter='';this.listPage=50;this.pageSize=50

      if(this.mode==='agent'&&this.ws&&this.ws.readyState===WebSocket.OPEN){
        this.ws.send(JSON.stringify({type:'query',command:this.command.trim()}))
        return
      }

      try{
        const res=await request({url:'/api/pub/redis/query',method:'post',data:{
          host:this.conn.host,port:Number(this.conn.port)||6379,
          password:this.conn.password,db:this.conn.db,command:this.command.trim()
        }})
        const d=res.data
        if(d.error){this.error=d.error}else{this.result=d}
      }catch(e){this.error=this.$t('tool.RedisQuery.errQuery')+': '+(e.message||'')}
      this.loading=false
    },
    copyResult(){
      let text
      if(this.resType==='list'){ text=JSON.stringify(this.resList,null,2) }
      else if(this.resType==='map'){ text=JSON.stringify(this.resMap,null,2) }
      else { text=this.resStr }
      navigator.clipboard.writeText(text)
      this.$message.success(this.$t("common.status.copied"))
    },
    doAction(a){
      if(a==='clear'){this.command='';this.result=null;this.error=''}
      if(a==='copy'){this.copyResult()}
    }
  }
}
</script>

<style scoped>
.rq-tool { display:flex;flex-direction:column;gap:14px; }

/* Card */
.rq-card { background:#fff;border:1px solid #ebeef5;border-radius:12px;padding:18px 22px; }
.rq-card-err { background:#fff5f5;border-color:#fde0e0; }
.rq-card-hd { display:flex;align-items:center;gap:8px;margin-bottom:14px;font-size:13px;font-weight:700;color:#1a1f36;flex-wrap:wrap; }
.rq-card-hint { font-size:11px;color:#b0b8c4;margin-left:auto;font-weight:400; }
.rq-dot { width:8px;height:8px;border-radius:50%;flex-shrink:0; }
.rq-dot-blue { background:#4e7cff; }
.rq-dot-purple { background:#6b3cf0; }
.rq-dot-green { background:#43a047; }

/* Badge */
.rq-badge { font-size:10px;font-weight:600;padding:2px 10px;border-radius:10px; }
.rq-badge-on { background:#e8f8e8;color:#43a047;margin-left:auto; }

/* Mode switch */
.rq-mode-switch { margin-left:auto; }

/* Connection form */
.rq-form { display:flex;flex-direction:column;gap:10px; }
.rq-field { display:flex;flex-direction:column;gap:4px; }
.rq-field label { font-size:12px;font-weight:600;color:#909399; }
.rq-field-btn { justify-content:flex-end; }
.rq-row { display:flex;gap:10px; }
.rq-row .rq-field { flex:1; }

/* Agent help */
.rq-agent-help { font-size:12px;color:#909399;background:#f0f9ff;border:1px solid #d0e8ff;border-radius:8px;padding:8px 12px;display:flex;align-items:center;gap:6px; }
.rq-agent-help i { color:#4e7cff; }
.rq-agent-dl { margin-left:auto;color:#4e7cff;font-weight:600;text-decoration:none; }
.rq-agent-dl:hover { text-decoration:underline; }

/* Commands */
.rq-tags { display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px; }
.rq-tag { cursor:pointer;font-family:'SF Mono',Consolas,monospace;font-size:12px; }
.rq-input-row { display:flex;gap:8px; }
.rq-cmd-input { flex:1; }
.rq-cmd-input >>> input { font-family:'JetBrains Mono',Consolas,monospace!important;font-size:13px!important; }
.rq-cmd-hint { font-size:11px;color:#b0b8c4;margin-top:6px; }

/* Result */
.rq-result-wrap { max-height:500px;overflow:auto; }
.rq-copy-btn { border:none;background:none;color:#b0b8c4;cursor:pointer;padding:2px 6px;font-size:14px;border-radius:4px; }
.rq-copy-btn:hover { color:#4e7cff;background:#f0f5ff; }

/* string */
.rq-str-meta { font-size:11px;color:#b0b8c4;margin-bottom:8px; }
.rq-str-val { font-family:'JetBrains Mono',Consolas,monospace;font-size:13px;color:#303133;word-break:break-all;white-space:pre-wrap; }
.rq-str-val pre { margin:0;white-space:pre-wrap;background:#f8f9fb;padding:12px;border-radius:8px;max-height:400px;overflow:auto;font-size:13px; }
.rq-str-fold { max-height:120px;overflow:hidden;position:relative; }
.rq-str-fold::after { content:'';position:absolute;bottom:0;left:0;right:0;height:40px;background:linear-gradient(transparent,#fff); }
.rq-str-acts { display:flex;gap:8px;margin-top:10px; }

/* type header */
.rq-type-hd { font-size:12px;color:#909399;margin-bottom:10px;display:flex;align-items:center;gap:10px; }
.rq-map-search { width:180px;margin-left:auto; }

/* list */
.rq-list { font-family:'JetBrains Mono',Consolas,monospace;font-size:13px; }
.rq-list-item { display:flex;gap:12px;align-items:flex-start;padding:6px 8px;border-radius:4px; }
.rq-list-item.rq-stripe { background:#f8f9fb; }
.rq-idx { color:#b0b8c4;min-width:40px;text-align:right;font-size:11px;flex-shrink:0;padding-top:1px; }
.rq-list-item code { color:#303133;word-break:break-all; }
.rq-more-btn { margin-top:10px;width:100%; }

/* map */
.rq-map-row:nth-child(even) td { background:#f8f9fb; }
.rq-empty { text-align:center;padding:24px;color:#b0b8c4;font-size:13px; }

/* nil */
.rq-nil { text-align:center;padding:20px;color:#b0b8c4;font-size:14px;font-style:italic; }

/* raw */
.rq-raw-val { font-family:'JetBrains Mono',Consolas,monospace;font-size:22px;color:#303133;padding:12px 0;word-break:break-all;text-align:center; }

.rq-table { width:100%;border-collapse:collapse;font-size:13px; }
.rq-table th { background:#f5f6f8;padding:8px 12px;text-align:left;font-weight:600;color:#1a1f36;border-bottom:2px solid #e4e7ed;position:sticky;top:0; }
.rq-table td { padding:7px 12px;border-bottom:1px solid #f0f2f5;color:#303133; }
.rq-table code { font-family:'JetBrains Mono',Consolas,monospace; }

.rq-err { font-size:13px;color:#f56c6c; }
</style>
